<template>
  <div>
    <datatable-bulk-actions
      v-if="selectedRows.length"
      v-model="actionTarget"
      type="author"
      :rows="selectedRows"
      :total="totalCount"
      :actions="bulkActions"
      @merge="onBulk('merge')"
      @edit="onBulk('edit')"
      @delete="onBulk('delete')"
      @cancel="closeModalAction"
    />
    <div class="author-table-top">
      <div class="author-search">
        <Search
          v-model="filters.name"
          :focus="true"
          :inside-card="true"
          @search="onSearch"
          @clearSearch="onSearch"
          class="mb20"
        />
      </div>
      <div class="card author-date">
        <date-range-picker
          name="from"
          :value="range"
          inputType="date"
          valueType="YYYY-MM-DD"
          :range="true"
          format="MMM DD YYYY"
          :clearable="true"
          range-separator=" - "
          @onInputChange="onDateInputChanged"
          calendarButtonIcon="fas fa-calendar-alt"
        >
      </date-range-picker>
    </div>
  </div>
  <div class="toggle-unused-author">
    <input-toggle
      :checked="filters.unused_author == 1"
      :onChange="onChangeUnusedAuthor"
      data-tip
      v-tooltip="getTooltipText()"
      :disabled="disableToggle"
    />
    <span>{{$t("author.include_unused_author")}}</span>
  </div>
    <div class="card">
      <div>
        <datatable
          :key="`table_list_${tableKey}`"
          ref="table"
          :data="datatable"
          :loading="loading"
          :updatingRow="updatingRowId"
          :genericRecords="totalCount"
          :bulk-edit="true"
        />
        <div class="loading-next-page" v-if="!loading && isBusy"><loader /></div>
        <side-panel v-if="showDeleteAuthor == true" :onClose="closeDeletePanel">
          <template>
            <load-content :loading="isBusy">
              <confirm-delete
                :label="deleteAuthorObj.first_name + (deleteAuthorObj.last_name ? ' ' + deleteAuthorObj.last_name : '')"
                :onDelete="() => deleteAuthor(deleteAuthorObj)"
              />
            </load-content>
          </template>
        </side-panel>
      </div>
    </div>


    <side-panel v-if="bulkEditOpen" :onClose="closeSidePanel">
      <template>
        <load-content :loading="isBusy">
          <div class="selected-label">
            <h3>Edit selected authors</h3>
            <p>All {{selectedLabel}} will be updated once you submit.</p>
          </div>
          <div class="form form--sidepanel">
          <div class="form-group">
            <label for="author-id">Author Approved?</label>
            <input-select
              name="author-id"
              :options="options"
              :onChange="(value) => onBulkUpdate('approved', value)"
              :selectedValue="options"
            />
          </div>
          </div>
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="bulkMergeOpen" :onClose="closeSidePanel">
        <template>
          <load-content :loading="isBusy">
            <div class="selected-label">
              <h3>Merge {{selectedLabel}} ?</h3>
              <p>The merge will combine multiple authors into one unified author. All articles in the selected authors will move to the unified author.</p>
            </div>
            <div class="form form--sidepanel">
            <div class="form-group">
              <label for="author-id">Merge Authors</label>
              <input-select
                id="categories"
                v-model="selectedRowId"
                :options="selectedRowsOptions"
                open-direction="bottom"
                :searchable="true"
            />
            </div>
            <div class="form__group field">
                  <input type="button" value="Save" class="btn btn-carma" @click="onMergeAuthors"/>
              </div>
            </div>
          </load-content>
        </template>
    </side-panel>
    <side-panel v-if="bulkDeleteOpen" :onClose="closeSidePanel">
      <confirm-delete
        :label="deleteSelectedLabel"
        :bulk="true"
        :onDelete="bulkDelete"
      />
    </side-panel>

  </div>
</template>

<script>
import Datatable from '../datatable';
import _ from 'lodash';
import InputSelect from '../form-components/input-select'
import LoadContent from '../load-content';
import { FIELD_TYPE, BULK_UPDATE } from "../../config/const";
import { propsForMixin } from "../../helpers/application";
import { mapState, mapActions } from 'vuex';
import SidePanel from "../side-panel";
import ConfirmDelete from "../confirm-delete";
import Search from "../../components/Search.vue";
import DateRangePicker from "../../components/date-picker-2";
import InputToggle from "../../components/form-components/input-toggle";
import moment from 'moment';
import Loader from "../../components/loader";
import DatatableBulkActions from '../../components/datatable-bulk-actions';

export default {
  name: 'AuthorsList',
  components: {
    datatable: Datatable,
    LoadContent,
    SidePanel,
    ConfirmDelete,
    InputSelect,
    Search,
    DateRangePicker,
    InputToggle,
    Loader,
    DatatableBulkActions,
  },
  created() {
    window.addEventListener('scroll', this.scroll);
    const { projectId, pagination } = this;
    this.getAuthors({ projectId , pagination })
        .then(() => this.isReady = true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scroll);
  },
  computed: {
    ...mapState({
      authors: state => state.admin.authors.authors.data,
      authorsPagination: state => state.admin.authors.authors.pagination,
      isBusy: state => state.admin.authors.authors.isBusy,
    }),
    deleteSelectedLabel() { return this.selectedLabel + ` (${this.selectedRows.length})`},
    selectedLabel() {
      return `${this.selectedRows.length} ${
        this.selectedRows.length == 1 ? "author" : "authors"
      } selected`;
    },
    selectedRowsOptions() {
      return this.selectedRows.map(option => ({
        value: option.id,
        label: this.getAuthorOptionLabel(option),
      }));
    },
    loading() {
      return this.isBusy === true && this.pagination.page === 1;
    },
    datatable() {
      const { authors } = this;
      const self = this;

      const body = authors || [];

      return {
        columns: [
          {
            key: 'id',
            label: "Author id",
          },
          {
            key: 'first_name',
            label: 'Name',
            orderable: true,
            input: {
              name: "first_name",
              type: FIELD_TYPE.INPUT,
              styleNames: {
                color: "#ec9148",
                border: "none",
                outline: "0px",
                fontSize: '14px',
              },
              onBlur: function(value, key, row) {
                self.updateInput(value, key, row);
              },
              readOnly: false,
            }
          },
          {
            key: 'last_name',
            label: 'Last Name',
            orderable: true,
            input: {
              name: "last_name",
              type: FIELD_TYPE.INPUT,
              styleNames: {
                color: "#ec9148",
                border: "none",
                outline: "0px",
                fontSize: '14px',
              },
              onBlur: function(value, key, row) {
                self.updateInput(value, key, row);
              },
              readOnly: false,
            }
          },
          {
            key: 'approved',
            label: "author approved",
            style: { 'min-width': '200px' },
            //orderable: true,
            input: {
              name: "approved",
              type: FIELD_TYPE.TOGGLE,
              onChange: function(value, key, row) {
                self.updateInput(value, key, row);
              }
            },
          },
          {
            key: 'article_count',
            label: 'nº associated articles ',
            orderable: true,
            render: ({article_count, id} = {}) => {
              return (article_count > 0 ? <router-link style="color:#ec9148;" to={`/analysis/projects/${this.$route.params.project_uid}/authors/${id}/articles?page=1`}>{article_count}</router-link> : <span>{article_count}</span>)
            }
          },
          {
            key: 'last_article',
            label: this.$t('author.table.last_article'),
            orderable: true,
            render: ({analysis_article_id, last_article_date} = {}) => {
              return (analysis_article_id ? <span>{moment.utc(last_article_date).format('YYYY-MM-DD')} <a style="color:#ec9148;" href={`/analysis/editor/${this.$route.params.project_uid}/${analysis_article_id}`} target="_blank"><i class="far fa-link"></i></a> </span>: null)
            }
          }
        ],
        body,
        removeRow: {
          enabled: true,
          onClick: function(row){
            self.openDeletePanel(row);
          }
        },
        order: {
          column: self.pagination.order,
          direction: self.pagination.sort,
          onChange: function(column, direction) {
            self.onSortTable({ order: column, sort: direction });
          }
        },
        bulkActions: {
          enabled: true,
          onSelect: function(rows) {
            self.selectedRows = rows;
          }.bind(this)
        },
      }
    },
    projectId() {
      return this.$route.params.project_uid;
    },
    range () {
      return [moment.utc(this.filters.fromDate).format('YYYY-MM-DD'), moment.utc(this.filters.toDate).format('YYYY-MM-DD')];
    },
    bulkActions () {
      const actions = [
        {
          type: 'merge',
          enabled: this.selectedRows.length > 1 && this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-layer-group'
        },
        {
          type: 'edit',
          enabled: this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-edit'
        },
        {
          type: 'delete',
          class: 'text-red',
          enabled: this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-trash'
        }
      ]
      return actions;
    },
    totalCount () {
      return (this.authorsPagination && this.authorsPagination.totalCount) || 0;
    },
  },
  methods: {
    ...mapActions({
      getAuthors: "admin/authors/getAuthors",
      updateAuthor: "admin/authors/updateAuthors",
      removeAuthor: "admin/authors/deleteAuthors",
      updateBulkAuthors: "admin/authors/updateBulkAuthors",
      mergeAuthors: "admin/authors/mergeAuthors",
      bulkDeleteAuthors: "admin/authors/bulkDeleteAuthors",
    }),

    onSearch() {
      this.pagination.page = 1;
      this.getAuthors({
        projectId: this.projectId,
        pagination: this.pagination,
        filters: this.filters
      })
    },

    getAuthorOptionLabel(author) {
      const name = author.last_name !== null ? author.first_name + ' ' + author.last_name : author.first_name;
      const countInfo = this.$tc("articles.count", author.article_count, {
        count: author.article_count,
      });

      return `Name: ${name} | ID: ${author.id} | ${countInfo}`;
    },

    onMergeAuthors: function () {
      const projectUid = this.projectUid;
      this.mergeAuthors({
        projectUid,
        authorId: this.selectedRowId,
        selectedAuthors: this.selectedRows
      })
        .then(() => {
          this.closeSidePanel();
          this.selectedRowId = null;
          this.selectedRows = [];
          this.tableKey++;
        });
    }, 
    updateInput(value, key, row) {
      this.updatingRowId = row.id;
      let newValue = {
        id: row.id,
        cat_id: row.cat_id,
        [key]: value
      }
      const projectId = this.projectId;
      this.updateAuthor({ projectId, row: newValue }).then(r => {
        this.updatingRowId = null;
      });
    },
    deleteAuthor(){
      const {projectUid, removeAuthor, closeDeletePanel, deleteAuthorObj: row} = this
      removeAuthor({projectUid, row}).then(() => {
        this.selectedRows = this.selectedRows.filter(item => item.id !== row.id);
        this.$refs.table.selectedRows = this.selectedRows;
        closeDeletePanel();
      });
    },

    onBulk: function(action) {
      if (action == "edit") {
        this.bulkEditOpen = true;
      }
      if (action == "merge") {
        this.bulkMergeOpen = true;
      }
      if(action == "delete"){
        this.bulkDeleteOpen = true;
      }
    },
    closeSidePanel: function() {
      this.bulkEditOpen = false;
      this.bulkMergeOpen = false;
      this.showMoreInformation = false;
      this.bulkDeleteOpen = false;
    },
    openDeletePanel: function(row){
      this.showDeleteAuthor = true;
      this.deleteAuthorObj = {...row};
    },
    closeDeletePanel: function(){
      this.showDeleteAuthor = false;
    },
    onBulkUpdate: function(field, value) {
      let rows = _.cloneDeep(this.selectedRows)
      let bulkAuthors = rows.map(author => {
        author[field] = value;
        return author;
      });
      const projectUid = this.projectUid;
      this.updateBulkAuthors({ projectUid, bulkAuthors });
      this.closeSidePanel();
      this.selectedRows = [];
      this.tableKey++;
    },
    onSortTable: async function({order, sort}) {
      let pagination = {};
      pagination.order = order;
      pagination.sort = sort;
      pagination.page = 1;
      pagination.perPage = this.pagination.perPage;
      this.pagination = pagination;
      this.getAuthors({projectId: this.projectId, pagination: this.pagination, filters: this.filters})
    },
    openSidePanel: function(row) {
      this.sourceEdit = { ...row };
      this.showMoreInformation = true;
    },
    onDateInputChanged: function(value) {
      this.filters.fromDate = value[0];
      this.filters.toDate = value[1];
      this.pagination.page = 1;
      this.disableToggle = false;
      if(!this.filters.toDate || !this.filters.fromDate){
        this.disableToggle = true;
        this.filters.unused_author = 0;
      }
      this.getAuthors({
        projectId: this.projectId,
        pagination: this.pagination,
        filters: this.filters
      })
    },
    onChangeUnusedAuthor: function(value) {
      this.pagination.page = 1;
      this.filters.unused_author = value == true ? 1 : 0;
      this.getAuthors({
        projectId: this.projectId,
        pagination: this.pagination,
        filters: this.filters
      })
    },
    getTooltipText: function() {
      if (this.disableToggle) {
        return this.$t('author.tooltips.unused_author_disabled');
      } else {
        return this.$t('author.tooltips.unused_author');
      }
    },
    scroll() {
      if (this.authorsPagination.currentPage < this.authorsPagination.totalPages) {
        const wHeight = window.innerHeight;
        const {
          top,
          height
        } = this.$refs.table.$el.getBoundingClientRect();
        const margin = 100;

        if (top * -1 + wHeight >= height - margin && this.isBusy == false) {
          this.pagination.page = this.pagination.page + 1
          const {projectId, pagination, filters} = this;
          this.getAuthors({projectId, pagination, filters})
        }
      }
    },
    bulkDelete: function(){
      const authorIds = this.selectedRows.map(ele=> ele.id);
      this.bulkDeleteAuthors({projectId: this.projectUid, author_ids: authorIds, pagination: this.pagination, filters: this.filters}).then(() => {
        this.closeSidePanel();
        this.selectedRows = [];
        this.getAuthors({projectId: this.projectUid, pagination: this.pagination, filters: this.filters});
      });
    },
    closeModalAction() {
      this.selectedRows = [];
      this.tableKey++;
    },
  },
  data: function() {
    const projectUid = this.$route.params.project_uid;
    return {
      filters: {
        name: '',
        fromDate: null,
        toDate: null,
        unused_author: false
      },
      isReady: false,
      tableKey: 0,
      counter: 0,
      projectUid,
      options: [
        { value: true, label: 'Approved' },
        { value: false, label: 'Not Approved' }
        ],
      selectedRowId: null,
      selectedRows: [],
      bulkEditOpen: false,
      pagination: {
        page: 1,
        perPage: 20,
        order: "author_id",
        sort: "asc"
      },
      bulkMergeOpen: false,
      selectedCategoryId: null,
      sourceEdit: {},
      showMoreInformation: false,
      filteredCategories: null,
      deleteAuthorObj: {},
      showDeleteAuthor: false,
      updatingRowId: null,
      disableToggle: true,
      actionTarget: BULK_UPDATE.ACTION_TARGET.SELECTED,
      bulkDeleteOpen: false
    }
  },
  mixins: [propsForMixin],
};
</script>

<style lang="scss" scoped>

.selected-label{
  margin-top: 30px;
}

.div-author {
  display: flex;
  flex-direction: row;
  margin-bottom: 25px;
  margin-top: 10px;
}

.author-table-top {
  display: flex;
  align-items: center;
}

.author-search,
.author-date {
  margin-right: 20px;
}
.author-date{ 
  max-width: 300px;
  margin-bottom: 20px;
  .mx-datepicker-range {
    &:deep(.mx-input) {
      border: 0;
      outline: 0;
      box-shadow: none;
      font-size: inherit;
      color: inherit;
      &:hover {
        background-color: #f1f1f1;
      }
    }
  }
}
.toggle-unused-author {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 5px;
  & > span {
      font-size: 13px;
      margin-left: 10px;
  }
}

  @import "../../styles/components/_datatable_bulk_actions.scss";
  @import "../../styles/components/coding-admin/_tags.scss";

  :deep(.source-affiliation-select.multiselect) {
    font-size: 0;
    padding: 0;
    margin: 0;
    min-height: 0;
    height: auto;

    .multiselect__select {
      top: calc(50% - 14px);
      line-height: 0;
    }

    .multiselect__tags {
      padding: 0;
      margin: 0;
      min-height: 0;
      border-radius: 5px;
      padding: 20px;

      .multiselect__single,
      .multiselect__placeholder {
        font-size: 16px;
        line-height: 1.4;
      }
    }
  }

</style>